/**
 * Aadhaar and PAN handling for the Employee Master fields.
 *
 * ── Why the browser validates these at all ─────────────────────────────────────────────────
 *
 * The User Management Lambda validates on write and is the authority — this does not replace
 * it. It exists for the same reason utils/phone.ts does: a malformed government identifier is
 * not a visible fault. The row looks complete, and the number is only discovered to be wrong
 * much later, by whoever files the payroll return with it.
 *
 * The rules here are deliberately identical to normalizeAadhaar / normalizePan in the
 * user-management Lambda. Two copies, because that Lambda is a separately zipped package with
 * no shared module with the frontend, and the same trade-off already applies to the phone
 * rule. A divergence would show up as a value the form accepts and the backend rejects.
 *
 * ── Both are OPTIONAL ──────────────────────────────────────────────────────────────────────
 *
 * Blank normalises to '' rather than failing. Most existing employees have neither recorded,
 * and a validator that treated "not yet collected" as an error would make every unrelated edit
 * to an old record impossible to save.
 */

/**
 * Aadhaar: exactly twelve digits.
 *
 * No letters, no separators. People habitually type it in "1234 5678 9012" groups, so spaces
 * and dashes are stripped before the test rather than rejected — the grouping is presentation,
 * not data, and refusing the way everybody writes it teaches admins to distrust the field.
 *
 * Deliberately NOT a Verhoeff checksum test. UIDAI numbers do carry one, and adding it would
 * catch single-digit typos this does not, but it also rejects the placeholder and test values
 * used while onboarding data is still being collected. The spec for this field is twelve
 * digits; if the checksum is wanted later it belongs here, as one extra guard, and it should
 * be added knowing it will reject values already in the table.
 */
const AADHAAR_PATTERN = /^\d{12}$/;

/**
 * PAN: five letters, four digits, one letter — "ABCDE1234F".
 *
 * The fourth character encodes holder type and the fifth is the surname initial, but neither is
 * checked: both have exceptions, and a validator that is right about the format and wrong about
 * the semantics rejects real cards.
 */
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

/** Separators people type into an identifier field that carry no information. */
const SEPARATORS = /[\s-]/g;

/**
 * The storable form of an Aadhaar number, '' when deliberately blank, or null when unusable.
 *
 * Null is what the callers turn into a validation message; '' is a legitimate "not recorded".
 */
export function normalizeAadhaar(value: string | null | undefined): string | null {
  const stripped = String(value ?? '').replace(SEPARATORS, '');
  if (stripped === '') return '';
  return AADHAAR_PATTERN.test(stripped) ? stripped : null;
}

/**
 * The storable form of a PAN, '' when deliberately blank, or null when unusable.
 *
 * Upper-cased before testing because PAN is case-insensitive in every context it is quoted in,
 * and an admin typing lowercase has made no error worth reporting.
 */
export function normalizePan(value: string | null | undefined): string | null {
  const stripped = String(value ?? '').replace(SEPARATORS, '').toUpperCase();
  if (stripped === '') return '';
  return PAN_PATTERN.test(stripped) ? stripped : null;
}

/** Whether a value is already in storable Aadhaar form. */
export const isAadhaar = (value: string | null | undefined): boolean =>
  typeof value === 'string' && AADHAAR_PATTERN.test(value);

/** Whether a value is already in storable PAN form. */
export const isPan = (value: string | null | undefined): boolean =>
  typeof value === 'string' && PAN_PATTERN.test(value);

/**
 * Aadhaar shown as "XXXX XXXX 9012" — the last four only.
 *
 * UIDAI's own guidance is that a stored Aadhaar is not displayed in full where it is not needed,
 * and a user table is exactly that: the admin scanning it wants to know whether a number is on
 * file, not what it is. The full value stays available in the edit form, which is a deliberate
 * action on one record rather than an incidental disclosure of everybody's.
 */
export const maskAadhaar = (value: string | null | undefined): string => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length !== 12) return '';
  return `XXXX XXXX ${digits.slice(8)}`;
};
